import React, { type HTMLAttributes } from 'react';
import type { RequestOptions } from 'searchkit';
import Immutable, { fromJS } from 'immutable';
import { useTranslation } from 'react-i18next';

import type {
  ImmutableEntity, ReferenceField, ParagraphEntity,
} from '@uncinc/react-drupal-core/types';
import { Paragraph, Block } from '@uncinc/react-drupal-core';
import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import type { QueryDslQueryContainer } from '@elastic/elasticsearch/lib/api/types';
import { useAspectRatio } from '../../../helpers/useAspectRatio';

import {
  DynamicMediaItem,
  Breadcrumbs,
  ButtonWrapper,
  Share,
} from '../../../components';
import MetaSearch from '../../../components/Search/MetaSearch';
import type { NodeAvoPreviousYearsPage } from '../../../types/uncinc_profile/nodes';
import { APP_OPTIONS, CONTENT_TYPE_AVO_PREVIOUS_YEARS_PAGE } from '../../../config/constants';

import {
  constructQuery,
} from '../../../components/Search/MetaSearch/metaParagraphsHelpers';

import './style.scss';

/**
 * Props for the AvoPreviousYearsPageFull component
 */
interface AvoPreviousYearsPageFullProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  /** Immutable entity containing the AVO previous years page data */
  entity: ImmutableEntity<NodeAvoPreviousYearsPage>;
}

/**
 * Detail page of AvoPreviousYearsPageFull.
 *
 * @param props - Component props
 * @param props.entity - Immutable entity containing the page data
 * @returns JSX element representing the AVO previous years page
 */
const AvoPreviousYearsPageFull = ({ entity }: AvoPreviousYearsPageFullProps) => {
  const { t } = useTranslation();

  // Extract page data from immutable entity
  const page = entity.toJS();
  const title = page.title[0].value;
  const paragraphs = page.field_paragraphs ?? [];
  const image = page.field_media_item[0];
  const year = page.field_year[0].value;

  // Content type for the search functionality
  const contentType = CONTENT_TYPE_AVO_PREVIOUS_YEARS_PAGE;

  // Configure search filters to find previous years (years less than current)
  const filters: QueryDslQueryContainer = {
    bool: {
      should: [],
      must: [
        {
          range: {
            year: {
              lt: year,
            },
          },
        },
      ],
    },
  };

  // Search request options for finding related AVO previous years
  const requestOptions: RequestOptions = {
    getBaseFilters: () => [
      constructQuery(contentType),
      filters,
    ],
  };

  // Define responsive aspect ratios for the hero image
  const aspectRatio: string = useAspectRatio({
    small: '4_3',
    medium: '4_3',
    large: '16_9',
    xlarge: '16_9',
  });

  const breadcrumbs = [
    {
      title: t('home'),
      url: '/',
    },
    {
      title: t('avo.previous_years'),
      url: APP_OPTIONS?.parameters.avoPreviousYearOverviewUri,
    },
  ];

  return (
    <div className="AvoPreviousYearsPageFull">
      <Container>
        {/* Breadcrumb navigation */}
        <div className="row">
          <div className="col-12 col-md-10 offset-md-1">
            <Breadcrumbs
              includeCurrent
              hideHome={false}
              // @ts-expect-error
              customCrumbs={fromJS(breadcrumbs)}
            />
          </div>
        </div>

        {/* Page header with title, hero image and body content */}
        <div className="row">
          <div className="col-12 col-md-10 offset-md-1">
            <div className="AvoPreviousYearsPageFull__title">
              <h1>{year} {title}</h1>
            </div>

            <div className="AvoPreviousYearsPageFull__hero">
              <DynamicMediaItem
                media={image}
                // @ts-expect-error
                imageAspectRatio={aspectRatio}
              />
            </div>
          </div>
        </div>
      </Container>

      {/* Dynamic paragraphs content */}
      {paragraphs.length > 0 && (
        <div className="AvoPreviousYearsPageFull__paragraphs">
          {paragraphs.map((item: ReferenceField<ParagraphEntity>) => {
            const paragraph = item.entity;
            const paragraphId = item.target_id;
            return (
              <Paragraph
                key={paragraphId}
                entity={Immutable.fromJS(paragraph)}
                id={paragraphId}
              />
            );
          })}
        </div>
      )}

      <Container>
        {/* Social sharing section */}
        <div className="row">
          <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
            <Share />
          </div>
        </div>

        {/* Call-to-action block for AVO previous years */}
        {/* TODO: Update to new design with image support */}
        <Block
          id={APP_OPTIONS.parameters.ctaBlockAvoPreviousYear}
          newBlock
        />

        {/* Related previous years section */}
        <div className="AvoPreviousYearsPageFull__related">
          <div className="AvoPreviousYearsPageFull__related__title">
            <h2>{t('avo.view_previous_years')}</h2>
          </div>
          <MetaSearch
            requestOptions={requestOptions}
            contentType={contentType}
            hitsPerPage={3}
            sortField="year"
            emptyState={<div>{t('search.no_results_related_avo_years')}</div>}
          />

          <ButtonWrapper
            className="AvoPreviousYearsPageFull__related__button"
            href={APP_OPTIONS.parameters.avoPreviousYearOverviewUri}
            inverted
          >
            {t('avo.more_previous_years')}
          </ButtonWrapper>
        </div>
      </Container>
    </div>
  );
};

export default AvoPreviousYearsPageFull;
